import {
  Card,
  Grid,
  Layout,
  Breadcrumbs,
  Heading,
  Form,
  Input,
  Spinner,
  Anchor,
  Modal,
} from '@enterprise-ui/canvas-ui-react';
import { getMap, getMapExt } from '../../app/neptune/neptuneSlice';
import { Link as ReactLink } from 'react-router-dom';
import { Button, SelectInput } from 'ui-library';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { SurveyMap } from '../../components/SurveyMap';
import {
  anyObstruction,
  CeilingMaterial,
  VestibuleBlockers,
  VestibuleLocation,
} from './options';
import { logEvent, LogLevel } from '@praxis/component-logging';
import {
  convertPhotosIntoFiles,
  obstructionConditional,
} from '../../utils/surveyHelpers';
import './styles/styles.scss';
import { useSurveyDispatch } from '../../hooks/useSurveyDispatch';
import { PhotoUploader } from '../../components/PhotoUploader';
import { useInstallerAuthContext } from '../../hooks/useInstallerAuthContext';
import { HeightFormField } from '../../components/HeightFormField';
import { convertFeetNumToDecimal } from '../../utils/helpers';

export const ATRTemplate = () => {
  /* Auth related constants */
  const { user } = useInstallerAuthContext();
  const dispatch = useDispatch();
  /* Navigation related constants */
  const location = useLocation()?.state;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // const vestibule_number = searchParams.get('vestibule');
  const floor_number = searchParams.get('floor');
  const [isPhotoMinHit, setIsPhotoMinHit] = useState(false);
  let [submitErr, setSubmitErr] = useState(false);
  let [isPhotoMaxHit, setIsPhotoMaxHit] = useState(false);
  /* Location State related data*/
  let { vestibuleValue, vestibuleLabel, coordinates, totalReaders } =
    location || {};
  let prevData = JSON?.parse(sessionStorage?.getItem(vestibuleValue));
  const store = location?.formData?.location_id;
  const [photoUploaded, setPhotoUploaded] = useState([]);
  const [isUpdateValid, setIsUpdateValid] = useState(false);
  const isPhotoArrEmpty = Boolean(
    photoUploaded.length && photoUploaded.every(Boolean),
  );
  const [formData, setFormData] = useState({
    ...location?.formData,
    ...prevData,
  });
  const response = useSelector((state) => state.neptune);
  const { error, loading } = useSelector((state) => state.vestibuleForm);

  const { dispatchEndpoint } = useSurveyDispatch(
    formData,
    photoUploaded,
    setIsPhotoMinHit,
  );
  const handleSubmit = async () => {
    try {
      setSubmitErr(false);
      await convertPhotosIntoFiles(photoUploaded, formData);
      await dispatchEndpoint();
      navigate(`/survey-success`, {
        state: {
          formData,
          vestibuleValue,
          vestibuleLabel,
          totalReaders,
        },
      });
    } catch (err) {
      logEvent({ message: err }, { level: LogLevel.Error });
      setSubmitErr(true);
    }
  };
  const canSubmit =
    [
      isUpdateValid,
      isPhotoArrEmpty,
      formData?.vestibule_type,
      formData?.floor_id,
      formData?.ceiling_height,
      formData?.is_obstructed_label,
      formData?.entrance_door_width,
      formData?.exit_door_width,
    ].every(Boolean) && obstructionConditional(formData);

  useEffect(
    function dispatchMapRequest() {
      if (store) {
        let storeId = store.substring(1);
        let floorNum = floor_number;
        if (user) {
          let { token } = user;
          dispatch(getMapExt({ storeId, floorNum, token }));
        } else {
          dispatch(getMap({ storeId, floorNum }));
        }
      }
    },
    [dispatch, store, floor_number, user],
  );

  //timeout after 20 secs
  useEffect(() => {
    if (!loading) return;

    const timer = setTimeout(() => {
      setSubmitErr(true);
    }, 20000);

    return () => clearTimeout(timer);
  }, [loading]);

  return (
    <>
      <Layout.Body data-testid="vestibuleFormPage" includeRail>
        <Card className="hc-pa-normal">
          <Grid.Container>
            <Breadcrumbs className="breadcrumbSpacing">
              <Breadcrumbs.Item as={ReactLink} to="/">
                Home
              </Breadcrumbs.Item>
              <Breadcrumbs.Item as={ReactLink} to="/survey">
                Pre-Install Survey
              </Breadcrumbs.Item>
            </Breadcrumbs>
            <Grid.Item xs={12}>
              <Heading size={3}>
                {' '}
                {formData?.vestibule_name} | Floor {floor_number} |{' '}
                {formData?.device_type}
              </Heading>
            </Grid.Item>
            {response?.error && (
              <Grid.Item xs={12}>
                <Card elevation={0}>
                  <Input.Info error>Map is currently unavailable.</Input.Info>
                </Card>
              </Grid.Item>
            )}
            {!response?.error && !response?.loading && coordinates && (
              <Grid.Item xs={12}>
                <SurveyMap
                  storeId={store}
                  floorNum={floor_number}
                  coordinates={coordinates}
                />
              </Grid.Item>
            )}
            {response?.loading && !response?.error && <Spinner />}
            {location?.formData && store ? (
              <Form
                role="form"
                id="registration_form"
                className="hc-mt-md"
                onSubmit={(e) => {
                  e.preventDefault();
                  sessionStorage.setItem(
                    vestibuleValue,
                    JSON?.stringify(formData),
                  );
                  handleSubmit();
                }}
              >
                <Grid.Container className="hc-pa-normal" align="center">
                  <Grid.Item xs={12}>
                    <SelectInput
                      data-testid="vestibule_type"
                      id="vestibule_type"
                      defaultValue={{
                        value: formData?.vestibule_type,
                        label: formData?.vestibule_type_label,
                      }}
                      classNamePrefix="customSelect"
                      label="Vestibule Type by Location"
                      name="vestibuleType"
                      required
                      options={VestibuleLocation()}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          vestibule_type: e?.value,
                          vestibule_type_label: e?.label,
                          floor_id: floor_number,
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <HeightFormField
                      label="Height of Ceiling:"
                      isChangeValid={setIsUpdateValid}
                      defaultValue={convertFeetNumToDecimal(
                        formData?.ceiling_height,
                      )}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          ceiling_height: e,
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <SelectInput
                      data-testid="ceiling_material"
                      id="ceiling_material"
                      label="Ceiling Material:"
                      classNamePrefix="customSelect"
                      defaultValue={{
                        value: formData?.ceiling_material,
                        label: formData?.ceiling_material,
                      }}
                      required
                      errorText="You must select an option"
                      options={CeilingMaterial()}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          ceiling_material: e?.value,
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <HeightFormField
                      label="Width of the Entrance:"
                      isChangeValid={setIsUpdateValid}
                      defaultValue={convertFeetNumToDecimal(
                        formData?.entrance_door_width,
                      )}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          entrance_door_width: e,
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <HeightFormField
                      label="Width of the Exit:"
                      isChangeValid={setIsUpdateValid}
                      defaultValue={convertFeetNumToDecimal(
                        formData?.exit_door_width,
                      )}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          exit_door_width: e,
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <SelectInput
                      data-testid="is_obstructed"
                      id="is_obstructed"
                      required
                      label="Are there any obstructions to the install?"
                      classNamePrefix="customSelect"
                      errorText="You must select an option"
                      defaultValue={{
                        value: formData.is_obstructed,
                        label: formData.is_obstructed_label,
                      }}
                      options={anyObstruction()}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          is_obstructed: Boolean(e?.value),
                          is_obstructed_label: e?.label,
                        });
                      }}
                    />
                  </Grid.Item>
                  {formData?.is_obstructed_label === 'Yes' && (
                    <Grid.Item xs={12}>
                      <SelectInput
                        label="Select your reason for the obstruction:"
                        classNamePrefix="customSelect"
                        data-testid="obstruction_reason"
                        id="obstruction_reason"
                        required
                        defaultValue={{
                          value: formData?.obstruction_reason,
                          label: formData?.obstruction_reason,
                        }}
                        options={VestibuleBlockers()}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            obstruction_reason: e?.value,
                          });
                        }}
                      />
                    </Grid.Item>
                  )}
                  {formData?.obstruction_reason === 'Other' &&
                    formData?.is_obstructed_label === 'Yes' && (
                      <Grid.Item xs={12}>
                        <Form.Field
                          data-testid="obstruction_other_reason"
                          classNamePrefix="customSelect"
                          id="obstruction_other_reason"
                          required
                          value={formData?.obstruction_other_reason}
                          label="Please type reason for being unable to install."
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              obstruction_other_reason: e?.target?.value,
                            });
                          }}
                        />
                      </Grid.Item>
                    )}
                  <Grid.Item xs={12}>
                    <Form.Field
                      data-testid="additional_notes"
                      value={formData?.additional_notes}
                      id="additional_notes"
                      type="textarea"
                      label="Other details if applicable"
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          additional_notes: e?.target?.value || '',
                        });
                      }}
                    />
                  </Grid.Item>
                  <Grid.Item xs={12}>
                    <PhotoUploader
                      files={photoUploaded}
                      onChange={setPhotoUploaded}
                      maximum={12}
                      minimum={2}
                      fileSizeLimit={5} //MB
                      isMaximumHit={isPhotoMaxHit}
                      isMinimumHit={isPhotoMinHit}
                      setMaxLimit={setIsPhotoMaxHit}
                      setMinLimit={setIsPhotoMinHit}
                    />
                  </Grid.Item>
                </Grid.Container>
                {loading && (
                  <Grid.Item xs={12} align="center" justify="center">
                    <Spinner />
                  </Grid.Item>
                )}
                <Grid.Item xs={12} align="center" justify="center">
                  <Button
                    className="hc-ma-normal"
                    label="Prev"
                    type="secondary"
                    name="prev_button"
                    id="prev_button"
                    onClick={() => {
                      sessionStorage.setItem(
                        vestibuleValue,
                        JSON?.stringify(formData),
                      );
                      navigate(`/survey-reader-list/?q=${store}`, {
                        state: { formData },
                      });
                    }}
                  />

                  <Button
                    className="hc-ma-normal"
                    label="Continue"
                    type="submit"
                    name="next_button"
                    id="submit"
                    disabled={!canSubmit}
                  />
                </Grid.Item>
                <Modal
                  isVisible={submitErr}
                  headingText={
                    error
                      ? 'Something went wrong...'
                      : 'This is taking longer than usual..'
                  }
                  onRefuse={() => setSubmitErr(false)}
                >
                  <Grid.Item xs={12} className="hc-pa-normal">
                    {error ? (
                      <>
                        <p className=" hc-mb-normal">
                          Failed to submit the survey form. Please try
                          resubmitting or or restarting your session.
                        </p>
                        <p className="hc-pb-expanded">
                          <em>{error}</em>
                        </p>
                      </>
                    ) : (
                      <>
                        <p className=" hc-mb-expanded">
                          This request is taking longer than expected and may
                          not complete -{' '}
                          <strong>large attachments can cause delays.</strong>
                        </p>
                        <p className=" hc-mb-expanded">
                          Please try again or upload smaller files.
                        </p>
                      </>
                    )}
                  </Grid.Item>
                </Modal>
              </Form>
            ) : (
              <Card elevation={0} className="hc-pa-normal cardContainer">
                <Input.Info error>
                  There was an issue loading the details for this location.
                  Please make sure{' '}
                  <Anchor to="/survey" as={ReactLink} className="iot-link">
                    previous information{' '}
                  </Anchor>{' '}
                  was submitted correctly.
                </Input.Info>
              </Card>
            )}
          </Grid.Container>
        </Card>
      </Layout.Body>
    </>
  );
};
